import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import {
  getDetailCustomFieldValue,
  getFieldValue,
  pickCustomFieldProps,
} from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import { isParentUser, isProviderUser } from '../../util/onboarding';
import { richText } from '../../util/richText';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  ExternalLink,
  NamedLink,
  ListingCard,
  Reviews,
  ReviewRating,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  NamedRedirect,
  CustomExtendedDataSection,
  ProviderContactBlock,
} from '../../components';

import ProviderAwareTopbar from '../ProviderTopbar/ProviderAwareTopbar';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

export const AsideContent = props => {
  const { user, displayName, showLinkToProfileSettingsPage } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {showLinkToProfileSettingsPage ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles, intl } = props;
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;

  const initialReviewState = !isProviderUserType
    ? REVIEW_TYPE_OF_CUSTOMER
    : REVIEW_TYPE_OF_PROVIDER;
  const [showReviewsType, setShowReviewsType] = useState(initialReviewState);

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const providerReviewsMaybe = isProviderUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsFromMyCustomersTitle"
                values={{ count: reviewsOfProvider.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeProviderSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
        },
      ]
    : [];

  const customerReviewsMaybe = isCustomerUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsAsACustomerTitle"
                values={{ count: reviewsOfCustomer.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeCustomerSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
        },
      ]
    : [];
  const desktopReviewTabs = [...providerReviewsMaybe, ...customerReviewsMaybe];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal
          className={css.desktopReviewsTabNav}
          tabs={desktopReviewTabs}
          ariaLabel={intl.formatMessage({ id: 'ProfilePage.screenreader.reviewsNav' })}
        />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig, intl } = props;

  const shouldPickUserField = fieldConfig =>
    ['public', 'metadata'].includes(fieldConfig?.scope) &&
    fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      userFieldConfig,
      'userType',
      shouldPickUserField
    ) || [];

  const pickUserFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, userTypeConfig = {}, showConfig = {} } = config;
    const { limitToUserTypeIds, userTypeIds } = userTypeConfig;
    const userType = publicData.userType;
    const isTargetUserType = !limitToUserTypeIds || userTypeIds.includes(userType);

    const { label, displayInProfile } = showConfig;
    const publicDataValue = getFieldValue(publicData, key);
    const metadataValue = getFieldValue(metadata, key);
    const value = publicDataValue !== null ? publicDataValue : metadataValue;

    if (displayInProfile && isTargetUserType && value !== null) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ProfilePage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };
  const sectionDetailsProps = {
    ...props,
    fieldConfigs: userFieldConfig,
    heading: 'ProfilePage.detailsTitle',
    rootClassName: css.userFieldSection,
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="profilePage"
      pickExtendedDataFields={pickUserFields}
      rootClassName={css.userFieldSection}
    />
  );
};

export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    userShowError,
    bio,
    displayName,
    user,
    listings,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    userTypeRoles,
    isProviderProfile,
    memberSinceYear,
    config,
  } = props;

  const hasListings = listings.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  // KidHive parent profiles are not public at all -- the page component renders NotFound for them
  // before MainContent is ever reached (a family's presence on the marketplace is private).

  // Inline social glyphs for the elsewhere-on-the-web row (client: icons instead of text).
  const SocialIcon = ({ type }) => {
    if (type === 'instagram') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2.2c3.2 0 3.58.01 4.85.07 3.25.15 4.77 1.69 4.92 4.92.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.15 3.23-1.66 4.77-4.92 4.92-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-3.26-.15-4.77-1.7-4.92-4.92C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85C2.38 3.92 3.9 2.38 7.15 2.23 8.42 2.17 8.8 2.16 12 2.16zm0 3.68a6.12 6.12 0 1 0 0 12.24 6.12 6.12 0 0 0 0-12.24zm0 10.09a3.97 3.97 0 1 1 0-7.94 3.97 3.97 0 0 1 0 7.94zm6.36-11.85a1.43 1.43 0 1 0 0 2.86 1.43 1.43 0 0 0 0-2.86z" />
        </svg>
      );
    }
    if (type === 'google') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M21.35 11.1H12v2.9h5.35c-.5 2.5-2.6 3.9-5.35 3.9a6 6 0 1 1 0-12c1.5 0 2.9.55 3.95 1.45l2.15-2.15A9 9 0 1 0 12 21c5.2 0 8.85-3.65 8.85-8.8 0-.4-.05-.75-.1-1.1z" />
        </svg>
      );
    }
    if (type === 'yelp') {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.5 2.6c-.3-.6-1.1-.7-1.6-.3L7.6 5.1c-.5.4-.5 1.2 0 1.6l5 4.3c.6.5 1.5.1 1.5-.7l-.1-6.6c0-.4-.2-.8-.5-1.1zM6.1 9.6c-.7-.2-1.4.3-1.4 1v4.3c0 .7.7 1.2 1.4 1l4.5-1.6c.7-.3.8-1.3.1-1.7L6.1 9.6zm5.6 6.5-3.5 3.2c-.5.5-.3 1.4.4 1.6l4.2 1.2c.7.2 1.3-.3 1.3-1v-4.3c0-.8-1-1.2-1.6-.7h-.8zm7.5-2.6-4.3 1.5c-.7.3-.8 1.3-.1 1.7l3.7 2.4c.6.4 1.4 0 1.5-.7l.6-3.9c.1-.7-.6-1.3-1.4-1z" />
        </svg>
      );
    }
    // website
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm7.93 9h-3.02a15.7 15.7 0 0 0-1.2-5.4A8.02 8.02 0 0 1 19.93 11zM12 4c.9 1.2 1.9 3.6 2.1 7H9.9c.2-3.4 1.2-5.8 2.1-7zM4.07 13h3.02c.14 1.96.55 3.8 1.2 5.4A8.02 8.02 0 0 1 4.07 13zm3.02-2H4.07a8.02 8.02 0 0 1 4.22-5.4 15.7 15.7 0 0 0-1.2 5.4zM12 20c-.9-1.2-1.9-3.6-2.1-7h4.2c-.2 3.4-1.2 5.8-2.1 7zm3.71-1.6c.65-1.6 1.06-3.44 1.2-5.4h3.02a8.02 8.02 0 0 1-4.22 5.4z" />
      </svg>
    );
  };

  // KidHive providers get a BUSINESS profile: the brand (business name, categories, ages served,
  // rating), the about text, their classes, reviews, and elsewhere-on-the-web links. Everything is
  // real profile data (publicData written from the hub Account page); nothing is invented.
  if (isProviderProfile) {
    const businessName = publicData?.businessName || displayName;
    const about = publicData?.businessDescription || bio;
    const categoryConfig = config?.categoryConfiguration?.categories || [];
    const categoryNames = (publicData?.activityCategories || [])
      .map(id => categoryConfig.find(c => c.id === id)?.name)
      .filter(Boolean);
    const ageGroups = Array.isArray(publicData?.ageGroups) ? publicData.ageGroups : [];
    const socialLinks = publicData?.socialLinks || {};
    const socialEntries = ['website', 'instagram', 'google', 'yelp'].filter(k => socialLinks[k]);
    const providerReviews = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
    const ratings = providerReviews
      .map(r => r?.attributes?.rating)
      .filter(rating => typeof rating === 'number');
    const avgRating =
      ratings.length > 0
        ? Math.round((ratings.reduce((sum, r) => sum + r, 0) / ratings.length) * 10) / 10
        : null;

    return (
      <div className={css.providerProfile}>
        <section className={css.providerHero}>
          {/* Below the large viewport the aside (avatar + name) is hidden, so the hero carries the
              avatar; on desktop the aside shows it and this one hides. Logo when uploaded;
              otherwise the shared Avatar shows the BUSINESS name's first letter (never the
              owner's personal initials -- userAbbreviatedName). */}
          <AvatarLarge className={css.heroAvatar} user={user} disableProfileLink />
          <H2 as="h1" className={css.providerName}>
            {businessName}
          </H2>
          {/* Founding Member badge (SOW deliverable): rendered when the operator has granted the
              status. metadata.foundingMember is public-readable, so it shows to everyone. */}
          {metadata?.foundingMember ? (
            <span className={css.foundingBadge}>
              <svg className={css.foundingBadgeIcon} viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 2.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9 6.8 18.6l1-5.8L3.5 8.7l5.9-.9z"
                  fill="currentColor"
                />
              </svg>
              <FormattedMessage id="ProfilePage.foundingMemberBadge" />
            </span>
          ) : null}
          <div className={css.providerMetaRow}>
            {avgRating != null ? (
              <span className={css.providerRating}>
                <ReviewRating
                  rating={Math.round(avgRating)}
                  reviewStarClassName={css.providerStar}
                />
                <span className={css.providerRatingValue}>{avgRating}</span>
                <span className={css.providerRatingCount}>
                  <FormattedMessage
                    id="ProfilePage.providerReviewCount"
                    values={{ count: providerReviews.length }}
                  />
                </span>
              </span>
            ) : null}
            {memberSinceYear ? (
              <span className={css.providerMemberSince}>
                <FormattedMessage id="ProfilePage.memberSince" values={{ year: memberSinceYear }} />
              </span>
            ) : null}
          </div>
          {categoryNames.length > 0 || ageGroups.length > 0 ? (
            <div className={css.providerChips}>
              {categoryNames.map(name => (
                <span key={name} className={css.providerChip}>
                  {name}
                </span>
              ))}
              {ageGroups.map(key => (
                <span key={key} className={classNames(css.providerChip, css.providerChipAge)}>
                  <FormattedMessage id={`ProviderOnboardingActivities.age.${key}`} />
                </span>
              ))}
            </div>
          ) : null}
        </section>

        {about ? (
          <section className={css.providerCard}>
            <H4 as="h2" className={css.providerCardTitle}>
              <FormattedMessage
                id="ProfilePage.providerAboutTitle"
                values={{ name: businessName }}
              />
            </H4>
            <p className={css.providerAbout}>
              {richText(about, {
                linkify: true,
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </p>
          </section>
        ) : null}

        {Array.isArray(publicData?.locations) && publicData.locations.length > 0 ? (
          <section className={css.providerCard}>
            <H4 as="h2" className={css.providerCardTitle}>
              <FormattedMessage
                id="ProfilePage.providerLocationsTitle"
                values={{ count: publicData.locations.length }}
              />
            </H4>
            <ul className={css.providerLocations}>
              {/* Each location renders the shared contact block (pin icon + address-with-
                  directions, phone icon + Call Us) -- same treatment as every other surface. */}
              {publicData.locations.map(loc => (
                <li key={loc.id} className={css.providerLocation}>
                  <ProviderContactBlock address={loc.address} phone={loc.phone || null} />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {hasListings ? (
          <section className={css.providerCard}>
            <H4 as="h2" className={css.providerCardTitle}>
              <FormattedMessage
                id="ProfilePage.providerClassesTitle"
                values={{ count: listings.length }}
              />
            </H4>
            <ul className={css.providerListings}>
              {listings.map(l => (
                // Display clarity: each card states the location the class is pinned to -- now
                // rendered INSIDE the card (showAddress) instead of dangling under it.
                <li className={css.providerListing} key={l.id.uuid}>
                  <ListingCard listing={l} showAuthorInfo={false} showAddress />
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {!hideReviews && providerReviews.length > 0 ? (
          <section className={css.providerCard}>
            <H4 as="h2" className={css.providerCardTitle}>
              <FormattedMessage
                id="ProfilePage.providerReviewsTitle"
                values={{ count: providerReviews.length }}
              />
            </H4>
            <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
            <Reviews reviews={providerReviews} />
          </section>
        ) : null}

        {socialEntries.length > 0 ? (
          <section className={css.providerCard}>
            <H4 as="h2" className={css.providerCardTitle}>
              <FormattedMessage id="ProfilePage.providerSocialTitle" />
            </H4>
            <div className={css.providerSocialRow}>
              {socialEntries.map(key => (
                <ExternalLink
                  key={key}
                  href={socialLinks[key]}
                  className={css.providerSocialIconLink}
                  title={intl.formatMessage({ id: `ProfilePage.providerSocial.${key}` })}
                >
                  <SocialIcon type={key} />
                </ExternalLink>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    );
  }

  return (
    <div>
      <H2 as="h1" className={css.desktopHeading}>
        <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: displayName }} />
      </H2>
      {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null}

      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
        />
      ) : null}

      {hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
          </H4>
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                <ListingCard listing={l} showAuthorInfo={false} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hideReviews ? null : isMobileLayout ? (
        <MobileReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
        />
      ) : (
        <DesktopReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
          intl={intl}
        />
      )}
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  // Providers never see the parent topbar; the banner below carries navigation back to the hub
  // (mirrors the class-page preview pattern).
  const isProviderViewer = isProviderUser(currentUser);
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};

  // Parent profiles get a minimal variant (name/avatar/bio/member-since). createdAt is a default
  // user attribute; guard against it being absent or unparseable.
  const isParentProfile = isParentUser(profileUser);
  const isProviderProfile = isProviderUser(profileUser);
  const createdAt = profileUser?.attributes?.createdAt;
  const memberSinceYear =
    createdAt && Number.isFinite(new Date(createdAt).getFullYear())
      ? new Date(createdAt).getFullYear()
      : null;
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (isParentProfile) {
    // Parent profiles are not public (client decision): a family's presence on the marketplace is
    // private, so the page is hidden from every viewer -- providers, other parents, and the parent
    // themself (parents manage their info in the account hub, not on a public page).
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSideNavigation
        sideNavClassName={css.aside}
        topbar={<ProviderAwareTopbar />}
        sideNav={
          <AsideContent
            user={profileUser}
            showLinkToProfileSettingsPage={mounted && isCurrentUser && !isProviderViewer}
            displayName={displayName}
          />
        }
        footer={<FooterContainer />}
      >
        {isProviderViewer ? (
          <div className={css.providerViewBanner}>
            <span className={css.providerViewText}>
              <FormattedMessage
                id={
                  isCurrentUser
                    ? 'ProfilePage.ownProfilePreview'
                    : 'ProfilePage.providerViewingProfile'
                }
              />
            </span>
            <span className={css.providerViewLinks}>
              <NamedLink name="ProviderDashboardPage" className={css.providerViewLink}>
                <FormattedMessage id="ProfilePage.backToDashboard" />
              </NamedLink>
              {isCurrentUser ? (
                <NamedLink name="ProviderAccountBasePage" className={css.providerViewLink}>
                  <FormattedMessage id="ProfilePage.editBusinessProfile" />
                </NamedLink>
              ) : null}
            </span>
          </div>
        ) : null}
        <MainContent
          bio={bio}
          displayName={displayName}
          user={profileUser}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          hideReviews={hasNoViewingRightsOnPrivateMarketplace}
          intl={intl}
          userTypeRoles={userTypeRoles}
          isProviderProfile={isProviderProfile}
          memberSinceYear={memberSinceYear}
          config={config}
          {...rest}
        />
      </LayoutSideNavigation>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
